import { ViewEntity } from '@view-model/domain/view/ViewEntity';
import { ViewName, ViewSetting } from '@view-model/domain/view';
import type { CommandManager, ICommand } from '@model-framework/command';
import { CompositeCommand, CreateCommand, UpdateCommand } from '@model-framework/command';
import { ObjectRepository, TextRepository } from '@framework/repository';
import { RTDBPath } from 'balus-common';
import type { Rect } from '@view-model/models/common/basic/Rect';
import { Point, Size } from '@view-model/models/common/basic';
import { PositionSetCreateCommand } from '@view-model/models/common/PositionSet/PositionSetCreateCommand';
import { PositionSet } from '@view-model/models/common/PositionSet';
import { PositionSetRepository } from '@view-model/models/common/PositionSet/infrastructure/PositionSetRepository';
import { StickyModel } from '@view-model/domain/model/StickyModel';
import { StickyModelContents } from '@view-model/domain/model/model-contents/StickyModelContents';
import type { PointJSON, ModelId, ViewId, ViewModelId } from 'balus-common';
import { ViewRepository } from '@view-model/infrastructure/view-model/ViewRepository';
import { StickyModelElementPositionMapRepository } from '@view-model/infrastructure/basic-model/StickyModelElementPositionMapRepository';
import { ModelCascadeRepository } from '@view-model/infrastructure/view-model/cascade/ModelCascadeRepository';
import { VIEW_GRID_SIZE, viewGridLayout } from '@view-model/ui/components/View/constants';
import type { ThemeColor } from '@view-model/models/common/color';
import { ModelCreateCommand } from '@view-model/command/model/ModelCreateCommand';

type CreateResult = {
  viewId: ViewId;
  modelId: ModelId;
  modelType: string;
};

export class ViewOperation {
  private readonly repository: ViewRepository;
  private readonly viewPositionRepository: ObjectRepository<PointJSON, Point>;

  public constructor(
    private readonly viewModelId: ViewModelId,
    private readonly viewId: ViewId,
    private readonly modelId: ModelId,
    private readonly commandManager: CommandManager
  ) {
    this.repository = new ViewRepository(viewModelId);
    this.viewPositionRepository = new ObjectRepository(Point, RTDBPath.View.positionPath(this.viewModelId, viewId));
  }

  /**
   * ビュー名の編集中（未確定）のテキスト内容をハンドルします。
   * @param name 編集中（未確定）のテキスト
   */
  async handleEditingNameChanged(name: ViewName): Promise<void> {
    const { viewModelId, viewId } = this;
    const nameRepository = new TextRepository(RTDBPath.View.namePath(viewModelId, viewId));

    await nameRepository.save(name.value);
  }

  /**
   * ビュー名の編集確定時のテキスト内容をハンドルします。
   * 確定時のテキスト内容が編集前と比較して変化していた場合はUndoヒストリに登録されます。
   *
   * @param name 編集確定後のテキスト
   * @param previousName 編集前のテキスト
   */
  async handleEditingNameConfirmed(name: ViewName, previousName: ViewName): Promise<void> {
    const trimmedName = name.trim();
    if (trimmedName.isEquals(previousName)) {
      return;
    }
    const { viewModelId, viewId } = this;

    const command = new UpdateCommand(
      previousName.value,
      name.value,
      new TextRepository(RTDBPath.View.namePath(viewModelId, viewId))
    );
    this.commandManager.execute(command);
  }

  async handleChangingViewColorConfirmed(color: ThemeColor, previousColor: ThemeColor): Promise<void> {
    const { viewModelId, viewId } = this;

    const viewColorUpdateCommand = new UpdateCommand(
      previousColor,
      color,
      new TextRepository(RTDBPath.View.themeColorPath(viewModelId, viewId))
    );

    this.commandManager.execute(viewColorUpdateCommand);
  }

  async handleUpdatingPositionChanged(position: Point): Promise<void> {
    return this.viewPositionRepository.save(position);
  }

  async handleUpdatingPositionConfirmed(position: Point, previousPosition: Point): Promise<void> {
    const snappedPosition = position.snapTo(viewGridLayout);
    const viewPositionUpdateCommand = new UpdateCommand(previousPosition, snappedPosition, this.viewPositionRepository);

    // スナップ後の座標が移動前と変化しない場合は、コマンドをただ実行する（Undoスタックに積まない）
    if (snappedPosition.isEqual(previousPosition)) {
      viewPositionUpdateCommand.do();
    } else {
      this.commandManager.execute(viewPositionUpdateCommand);
    }
  }

  async handleResizingConfirmed(
    newViewRect: Rect,
    oldViewRect: Rect,
    callback: (snappedViewRect: Rect) => void
  ): Promise<void> {
    const { modelId, viewModelId } = this;
    const modelContents = await new ModelCascadeRepository(viewModelId).load(modelId);
    if (!modelContents) return;

    const snappedViewRect = newViewRect.snapTo(viewGridLayout);
    const compositeCommand = await this.buildResizeCommand(snappedViewRect, oldViewRect, modelContents);

    // スナップ後の座標・サイズが変化していない場合はUndoスタックに積まずにコマンドを実行する
    if (snappedViewRect.isEqual(oldViewRect)) {
      compositeCommand.do();
    } else {
      this.commandManager.execute(compositeCommand);
    }

    callback(snappedViewRect);
  }

  private async buildResizeCommand(
    viewRect: Rect,
    previousViewRect: Rect,
    modelContents: StickyModelContents
  ): Promise<CompositeCommand> {
    const { viewModelId, viewId, modelId } = this;
    const viewCollection = await this.repository.loadCollection();
    const view = viewCollection.findById(this.viewId) as ViewEntity;
    let parentViewSizeUpdateCommand: UpdateCommand<Size> | undefined;
    if (view.viewType === 'meaningPanel') {
      // 意味の構造パネルのビューの場合、親ビューの高さも更新する
      const parentView = viewCollection.findByMeaningPanelViewId(view.id) as ViewEntity;
      parentViewSizeUpdateCommand = new UpdateCommand(
        parentView.size,
        new Size(parentView.size.width, viewRect.height),
        new ObjectRepository(Size, RTDBPath.View.sizePath(viewModelId, parentView.id))
      );
    }
    const sizeUpdateCommand = new UpdateCommand(
      previousViewRect.getSize(),
      viewRect.getSize(),
      new ObjectRepository(Size, RTDBPath.View.sizePath(viewModelId, viewId))
    );

    // ビューが意味の構造パネルを持つ場合、そのパネルの位置とサイズも更新する
    let meaningPanelViewPositionUpdateCommand: UpdateCommand<Point> | undefined;
    let meaningPanelViewSizeUpdateCommand: UpdateCommand<Size> | undefined;
    if (view.meaningPanelViewId) {
      const meaningPanelView = viewCollection.findById(view.meaningPanelViewId) as ViewEntity;
      meaningPanelViewPositionUpdateCommand = new UpdateCommand(
        previousViewRect.topRight(),
        viewRect.topRight(),
        new ObjectRepository(Point, RTDBPath.View.positionPath(this.viewModelId, meaningPanelView.id))
      );
      meaningPanelViewSizeUpdateCommand = new UpdateCommand(
        meaningPanelView.size,
        new Size(meaningPanelView.size.width, viewRect.height),
        new ObjectRepository(Size, RTDBPath.View.sizePath(this.viewModelId, meaningPanelView.id))
      );
    }

    const viewPositionUpdateCommand = new UpdateCommand(
      previousViewRect.topLeft(),
      viewRect.topLeft(),
      this.viewPositionRepository
    );

    if (modelContents instanceof StickyModelContents) {
      // 付箋モデルだけの特別対応。
      // モデル要素の位置はビューのサイズ変更に関わらず画面上の現在表示位置を維持するように、ビューのサイズ変更に合わせて移動する
      // （モデル要素の位置はビューの左上を原点とした相対座標のため、移動しないとビューの左上にくっつくように画面上は移動して見える）
      const contentsOffset = previousViewRect.topLeft().subtract(viewRect.topLeft());
      const moveContentsCommand = this.buildMoveContentsCommand(modelId, modelContents, contentsOffset);

      if (moveContentsCommand) {
        // 意味の構造パネルのCommandがある場合それも含める
        if (meaningPanelViewPositionUpdateCommand && meaningPanelViewSizeUpdateCommand) {
          return new CompositeCommand(
            sizeUpdateCommand,
            viewPositionUpdateCommand,
            meaningPanelViewPositionUpdateCommand,
            meaningPanelViewSizeUpdateCommand,
            moveContentsCommand
          );
        } else {
          return new CompositeCommand(sizeUpdateCommand, viewPositionUpdateCommand, moveContentsCommand);
        }
      }
    }
    // 意味の構造パネルのCommandがある場合それも含める
    if (meaningPanelViewPositionUpdateCommand && meaningPanelViewSizeUpdateCommand) {
      return new CompositeCommand(
        sizeUpdateCommand,
        viewPositionUpdateCommand,
        meaningPanelViewPositionUpdateCommand,
        meaningPanelViewSizeUpdateCommand
      );
    }
    // このViewが意味の構造パネルの場合、親ビューのサイズ更新も含める
    if (parentViewSizeUpdateCommand) {
      return new CompositeCommand(sizeUpdateCommand, viewPositionUpdateCommand, parentViewSizeUpdateCommand);
    }
    return new CompositeCommand(sizeUpdateCommand, viewPositionUpdateCommand);
  }

  private buildMoveContentsCommand(
    modelId: ModelId,
    modelContents: StickyModelContents,
    contentsOffset: Point
  ): ICommand | null {
    const { viewModelId } = this;

    if (contentsOffset.x === 0 && contentsOffset.y === 0) return null;

    // TODO: StickyModelの要素がViewに入り込んでいるのでその整理
    //
    // クリティカルな問題の修正のため一時的にこの場所でStickyModel関連のリポジトリを取得して利用している
    // （実装はStickyModelContentsOperation から拝借）
    // https://github.com/levii/balus-app/issues/2526
    const positionMapRepository = new StickyModelElementPositionMapRepository(viewModelId, modelId);

    const commentPositionSetRepository = new PositionSetRepository(
      RTDBPath.Comment.positionsPath(viewModelId, modelId)
    );
    const descriptionPanelPositionSetRepository = new PositionSetRepository(
      RTDBPath.DescriptionPanel.positionsPath(viewModelId, modelId)
    );
    const fileContainerPositionSetRepository = new PositionSetRepository(
      RTDBPath.FileContainer.positionsPath(viewModelId, modelId)
    );

    return modelContents.buildMoveCommand(
      contentsOffset.x,
      contentsOffset.y,
      positionMapRepository,
      commentPositionSetRepository,
      descriptionPanelPositionSetRepository,
      fileContainerPositionSetRepository
    );
  }

  handleSettingChanged(setting: ViewSetting): Promise<ViewSetting> {
    return this.repository.saveSetting(this.viewId, setting);
  }

  async addMeaningPanelView(): Promise<CreateResult> {
    const viewCollection = await this.repository.loadCollection();
    const targetView = viewCollection.findById(this.viewId) as ViewEntity;
    const targetViewPosition = (await this.viewPositionRepository.get()) as Point;
    // 空のViewを作成
    const model = StickyModel.buildNew();
    const modelContents = StickyModelContents.buildEmpty(model.key, model.version);

    const meaningPanelView = ViewEntity.buildNew({
      name: new ViewName(`meaning panel: ${this.viewId}`),
      modelKey: modelContents.key,
      // MeaningPanel の初期サイズは View の初期サイズ (VIEW_GRID_SIZE*7) のほぼ半分に設定
      size: new Size(VIEW_GRID_SIZE * 4, targetView.size.height),
      setting: ViewSetting.buildNew(),
      viewType: 'meaningPanel',
      meaningPanelIsOpen: true,
    });

    const viewCreateCommand = new CreateCommand(
      meaningPanelView,
      new ObjectRepository(ViewEntity, RTDBPath.View.viewPath(this.viewModelId, meaningPanelView.id))
    );
    const viewPositionCreateCommand = new PositionSetCreateCommand(
      new PositionSet({ [meaningPanelView.id]: targetView.getRect(targetViewPosition).topRight() }),
      RTDBPath.View.positionsPath(this.viewModelId)
    );

    const modelCascadeRepository = new ModelCascadeRepository(this.viewModelId);
    const modelCreateCommand = new ModelCreateCommand(modelCascadeRepository, modelContents);

    const newTargetView = targetView.cloneWithNewMeaningPanelViewId(meaningPanelView.id);
    const targetViewUpdateCommand = new UpdateCommand(
      targetView,
      newTargetView,
      new ObjectRepository(ViewEntity, RTDBPath.View.viewPath(this.viewModelId, targetView.id))
    );

    const command = new CompositeCommand(
      modelCreateCommand,
      viewCreateCommand,
      viewPositionCreateCommand,
      targetViewUpdateCommand
    );

    this.commandManager.execute(command);

    return {
      viewId: meaningPanelView.id,
      modelId: model.id,
      modelType: model.type,
    };
  }

  async changeMeaningPanelOpenState(targeteaningPanelId: ViewId, isOpen?: boolean): Promise<void> {
    const viewCollection = await this.repository.loadCollection();
    const targetMeaningPanel = viewCollection.findById(targeteaningPanelId) as ViewEntity;
    if (!targetMeaningPanel || targetMeaningPanel.viewType !== 'meaningPanel') return;

    // isOpen の指定がなければtoggle動作
    const updatedView = targetMeaningPanel.cloneWithNewMeaningPanelIsOpen(
      isOpen ?? !targetMeaningPanel.meaningPanelIsOpen
    );
    const updateCommand = new UpdateCommand(
      targetMeaningPanel,
      updatedView,
      new ObjectRepository(ViewEntity, RTDBPath.View.viewPath(this.viewModelId, targetMeaningPanel.id))
    );
    this.commandManager.execute(updateCommand);
  }
}
